import React, { useCallback, useState } from 'react';
import { useStyles } from './TruckCard.styles';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import { Box, Dialog, DialogContent } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteRounded';
import CheckIcon from '@material-ui/icons/CheckCircleOutline';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import Button from '@material-ui/core/Button';
import { CustomButton, UploadDocuments } from 'components';
import uploadIconSrc from 'assets/icons/upload_primary.svg';
import closeIconSrc from 'assets/svg/close.svg';
import { GetDocumentsQuery, GetDocumentTypesQuery } from 'generate/typesDMS';
import { getSupplyDrivers, supplyServiceClient } from 'graphqlapi';
import { useModalStateControls, useReadUserInfo, useResultContext } from 'hooks';
import { Driver, useDeleteDriverMutation } from 'generate/typesSupplyService';

const DOC_TYPES = ["driver's license", "identity card"];

export const DriverCard = ({
	active,
	assetDocumentsData,
	documentTypesData,
	id,
	idNumber,
	names,
	phone,
	refetchDocuments,
}: Driver & {
	documentTypesData?: GetDocumentTypesQuery;
	assetDocumentsData?: GetDocumentsQuery;
	refetchDocuments?: () => void;
}) => {
    const {t} = useTranslation('driver');
	const { t: tB } = useTranslation('buttons');
	const { t: tM } = useTranslation('modals');

    const classes = useStyles();
	const userInfo = useReadUserInfo();
	const { toggleResult, openErrorResult } = useResultContext();

    const status = active === true || active === null;
	const { isOpened, toggleModal } = useModalStateControls();
	const [open, setOpen] = useState(false);

	const assetDocumentTypes = (documentTypesData?.documentTypes || []).filter(
		documentType => (
			documentType?.label && DOC_TYPES.some(
				docType => documentType?.label?.toLowerCase().includes(docType)
			)
		)
	);

	const driverDocuments = (assetDocumentsData?.getDocuments || []).filter(
		driverDocument => driverDocument?.driverId === parseInt(id, 10)
	);

	const [deleteDriverMutation, { loading }] = useDeleteDriverMutation({
		client: supplyServiceClient,
		onCompleted: ({ deleteDriver }) => {
			toggleResult({
				text: deleteDriver?.message || tM('asset_deleted', { serial: idNumber }),
			});
		},
		onError: (error) => {
			openErrorResult(error, {
				title: tM('oops'),
				text: error?.message || '',
				type: 'error',
			});
		},
		refetchQueries: [
		  {
			query: getSupplyDrivers,
			variables: { transporterId: userInfo?.transporter_id, first: 100 },
		  },
		],
		awaitRefetchQueries: true,
	});

	// @ts-ignore
	const handleSubmit = useCallback(
		(id) => {
			deleteDriverMutation({
				variables: { id }
			})
		},
		[deleteDriverMutation]
	);

	const handleOpenDialogue = () => {
		setOpen(true);
	};

	const handleCancel = () => {
		setOpen(false);
	};

    return (
        <ul className={classes.card}>
            <li className={classes.item}>
				<span>
					<Box display="flex" flexDirection="column">
						<Typography className={classes.title} variant="caption" component="p">
							{t('driver_name')}
						</Typography>
						<Typography className={classes.value} variant="body1" component="p">
							{names}
						</Typography>
					</Box>
					<Box display="flex" flexDirection="row" alignItems="center">
						<Typography className={classes.itemInfoLabel} color="inherit" variant="caption" component="p">
							{t('id_number')}
						</Typography>
						<Typography className={classes.itemInfoText} color="textPrimary" variant="body1" component="p">
							{idNumber}
						</Typography>
					</Box>
					<Box display="flex" flexDirection="row" alignItems="center">
						<Typography className={classes.itemInfoLabel} color="inherit" variant="caption" component="p">
							{t('phone_number')}
						</Typography>
						<Typography className={classes.itemInfoText} color="textPrimary" variant="body1" component="p">
							{phone}
						</Typography>
					</Box>
				</span>
				<div className={classes.extras}>
					<Button
						className={classes.iconButton}
						onClick={() => toggleModal()}
						startIcon={
							<img
								src={uploadIconSrc}
								alt='Upload Documents'
								className={classes.uploadIconButton}
							/>
						}
					/>
					<IconButton
						onClick={() => handleOpenDialogue()}
						className={classes.deleteIconButton}
						disabled={status}
					>
						<DeleteIcon />
					</IconButton>
					<Box
						className={status ? classes.activeStatus : classes.inactiveStatus}
						display="flex"
						alignItems="center"
					>
						{status ? <CheckIcon /> : <InfoIcon />}
						<Typography className={classes.statusText} variant="body1" component="p">
							{status ? 'Active' : 'Inactive'}
						</Typography>
					</Box>
					{isOpened && (
						<UploadDocuments
							// @ts-ignore
							transporterDocuments={driverDocuments || []}
							onClose={toggleModal}
							// @ts-ignore
							documentTypes={assetDocumentTypes || []}
							refetchDocuments={refetchDocuments}
							assetType='driver'
							assetId={parseInt(id, 10)}
						/>
					)}
					<Dialog open={open} className={classes.deleteAssetDialog}>
						<DialogContent>
							<Box textAlign='center' marginX='auto' marginBottom={2}>
								<img src={closeIconSrc} alt="close" />
							</Box>
							<Typography className={classes.deleteAsset}>
								{tM('delete_driver', { idNumber })}
							</Typography>
							<Box display='flex' flexDirection='column' marginY={3}>
								<CustomButton
									className={cn(classes.btn, classes.modalDismissButton)}
									onClick={handleCancel}
									data-qa="qa-close"
									variant='outlined'
								>
									{tB('no')}
								</CustomButton>
								<CustomButton
									className={cn(classes.btn, classes.confirmDelete)}
									data-qa="qa-proceed"
									type='submit'
									onClick={() => handleSubmit(id)}
									disabled={loading}
								>
									{tB('yes')}
								</CustomButton>
							</Box>
						</DialogContent>
					</Dialog>
				</div>
            </li>
        </ul>
    );
};
